/**
 * DashboardService
 * 
 * Service for the Portfolio Monitor page.
 * Provides methods to list backtests and fetch config/results by name.
 */

import config from '../config/api';
import { BacktestRequest } from '../features/order-ticket/strategy-backtest/config/types';
import { BacktestSeriesPayload } from '../components/utils/returns';

export interface CompletedBacktestSummary {
  backtest_name: string;
  backtest_config_id: string;
  results_data_id: string;
  start_date: string;
  end_date: string;
  total_pnl: number;
}

export interface ListBacktestDataResponse {
  success: boolean;
  backtests: CompletedBacktestSummary[];
  count: number;
  error?: string;
}

export interface BacktestConfigByNameResponse {
  success: boolean;
  backtest_name: string;
  backtest_config_id?: string;
  config_data?: BacktestRequest;
  error?: string;
}

export interface BacktestResultsByNameResponse {
  success: boolean;
  backtest_name: string;
  backtest_config_id?: string;
  results_data_id?: string;
  results?: BacktestSeriesPayload;
  error?: string;
}

export class DashboardService {
  private baseUrl = config.apiUrl;

  /**
   * List all completed backtests for a user with summary data.
   */
  async listBacktests(
    userUuid: string,
    token: string
  ): Promise<ListBacktestDataResponse> {
    const response = await fetch(
      `${this.baseUrl}/user/${userUuid}/backtests/data`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `Failed to list backtests: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Fetch backtest configuration by name.
   */
  async getBacktestConfigByName(
    userUuid: string,
    token: string,
    backtestName: string
  ): Promise<BacktestConfigByNameResponse> {
    const response = await fetch(
      `${this.baseUrl}/user/${userUuid}/backtests/${encodeURIComponent(backtestName)}/config`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `Failed to get backtest config: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Fetch backtest results by name.
   */
  async getBacktestResultsByName(
    userUuid: string,
    token: string,
    backtestName: string
  ): Promise<BacktestResultsByNameResponse> {
    const response = await fetch(
      `${this.baseUrl}/user/${userUuid}/backtests/${encodeURIComponent(backtestName)}/results`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `Failed to get backtest results: ${response.status}`);
    }

    return response.json();
  }
}

// Export singleton instance
export const dashboardService = new DashboardService();
